import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { AppComponent } from '../../app.component';
import { BaseService } from '../../shared/services/base.service';

import { Crew } from '../../shared/models/crew.interface';
import { Job } from '../../shared/models/job.interface';
import { JobSummary } from '../models/job.summary.interface';
import { User } from '../../shared/models/user.interface';
import { WorkOrderEdit } from '../models/work.order.edit.interface';
import { WorkOrderType } from '../../shared/models/work.order.type.interface';
import { JobFilter } from '../models/job.filter';
import { ChangeOrder } from '../../shared/models/change.order.interface';
import { Area } from '../../shared/models/area.interface';
import { JobCostEdit } from '../models/job.cost.edit.interface';
import { ChangeOrderSummary } from '../models/change.order.summary';
import { ChangeOrderItemSummary } from '../models/change.order.item.summary';
import { ChangeOrderItem } from '../../shared/models/change.order.item.interface';
import { DealerJobSummary } from '../list/list.component';
import { Router } from '@angular/router';
import { TaxOptionsDialogData } from '../../shared/models/taxoptions.dialog.interface';
import { PrintChangeOrderOptions } from '../models/print.change.order.options.interface';
import { JobCustomItem } from '../../shared/models/job.custom.item.interface';
import { NotificationData } from '../arrival-notification-dialog/arrival-notification-dialog';

@Injectable()

export class JobService extends BaseService {

  constructor(private appComponent: AppComponent, protected http: HttpClient, router: Router) {
    super(http, router);
  }

  getJob(jobId): Observable<JobSummary> {
    return this.httpGet<JobSummary>("/job/" + jobId);
  }

  getChangeOrderSummary(changeOrderId: number): Observable<ChangeOrderSummary> {
    return this.httpGet<ChangeOrderSummary>("/job/changeOrder/" + changeOrderId);
  }

  getChangeOrderItemSummary(changeOrderId: number, quoteAreaId: number, areaId: number, changeOrderItemId: number): Observable<ChangeOrderItemSummary> {
    let params = new HttpParams();
    params = params.append('changeOrderId', changeOrderId);
    params = params.append('quoteAreaId', quoteAreaId === undefined ? null : quoteAreaId);
    params = params.append('areaId', areaId === undefined ? null : areaId);
    params = params.append('changeOrderItemId', changeOrderItemId === undefined ? null : changeOrderItemId)

    return this.httpGet<ChangeOrderItemSummary>("/job/changeOrderItem?" + params.toString());
  }

  getSummary(filter: JobFilter): Observable<DealerJobSummary> {
    let params = new HttpParams();

    if (filter.projectName) params = params.append('projectName', filter.projectName);
    if (filter.convertedDateRange) params = params.append('convertedDateRange', filter.convertedDateRange.toString());
    if (filter.startDateRange) params = params.append('startDateRange', filter.startDateRange.toString());
    if (filter.jobTypeId) params = params.append('jobTypeId', filter.jobTypeId.toString());
    if (filter.salespersonId) params = params.append('salespersonId', filter.salespersonId.toString());
    if (filter.statusId) params = params.append('statusId', filter.statusId.toString());

    return this.httpGet<DealerJobSummary>("/job/summary?" + params.toString());
  }

  getList(filter: JobFilter, sortColumn: string, sortDirection: string, page: number): Observable<Job[]> {
    let params = new HttpParams();

    if (page != null) params = params.append('page', page.toString());
    if (sortColumn != null) params = params.append('sortColumn', sortColumn);
    if (sortDirection != null) params = params.append('sortDirection', sortDirection);
    if (filter.projectName) params = params.append('projectName', filter.projectName);
    if (filter.convertedDateRange) params = params.append('convertedDateRange', filter.convertedDateRange.toString());
    if (filter.startDateRange) params = params.append('startDateRange', filter.startDateRange.toString());
    if (filter.salespersonId) params = params.append('salespersonId', filter.salespersonId.toString());
    if (filter.jobTypeId) params = params.append('jobTypeId', filter.jobTypeId.toString());
    if (filter.statusId) params = params.append('statusId', filter.statusId.toString());

    return this.httpGet<Job[]>("/job?" + params.toString());
  }

  getJobList(): Observable<Job[]> {
    return this.httpGet<Job[]>("/job/joblist");
  }

  getSalespersons(): Observable<User[]> {
    return this.httpGet<User[]>("/user/salesperson");
  }

  getCrews(): Observable<Crew[]> {
    return this.httpGet<Crew[]>("/workOrder/crew");
  }

  getAreas(): Observable<Area[]> {
    return this.httpGet<Area[]>("/quote/area");
  }

  getWorkOrderTypes(): Observable<WorkOrderType[]> {
    return this.httpGet<WorkOrderType[]>("/workOrder/workOrderType");
  }

  approveChangeOrder(changeOrderId: number, bidAmount: number, acceptedAreas: number[]): Observable<number> {
    let data = {
      BidAmount: bidAmount,
      AcceptedAreas: acceptedAreas
    };
    return this.httpPost<number>("/job/approve/" + changeOrderId, data);
  }

  addWorkOrder(quoteId: number, workOrder: WorkOrderEdit): Observable<number> {
    workOrder.areas = null;

    if (workOrder.workOrder_ID == null)
      return this.httpPut<number>("/workOrder/" + quoteId, workOrder);
    else
      return this.httpPost<number>("/workOrder/" + workOrder.workOrder_ID, workOrder);
  }

  saveJobAddress(job: Job): Observable<number> {
    return this.httpPut<number>("/job/address/" + job.quote_ID, job);
  }

  deleteWorkOrder(workOrderId: number): Observable<number> {
    return this.httpDelete<number>("/workOrder/" + workOrderId);
  }

  saveCustomItem(quoteId: number, customItem: JobCustomItem): Observable<number> {
    if (customItem.jobCustomItem_ID === null || customItem.jobCustomItem_ID === undefined)
      return this.httpPost<number>(`/job/customItem/${quoteId}`, customItem);
    else
      return this.httpPut<number>(`/job/customItem/${customItem.jobCustomItem_ID}`, customItem);
  }

  getCustomItem(quoteId: number, customItemID: number): Observable<JobCustomItem> {

    return this.httpGet<JobCustomItem>(`/job/customItem/${customItemID}/quote/${quoteId}`);
  }

  //editCustomItem(customItem: JobCustomItem): Observable<number> {
  //  return this.httpPut<number>(`/job/customItem/${customItem}`, customItem);
  //}

  removeCustomItem(customItemId: number): Observable<number> {
    return this.httpDelete<number>(`/job/customItem/${customItemId}`);
  }

  printChangeOrder(changeOrderId: number, printOptions: PrintChangeOrderOptions): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .set('timezone', timezone.toString())
      .append('locale', printOptions.locale)
      .append('showPricing', printOptions.showPricing)
      .append('showSquareFootage', printOptions.showSqrFt)
      .append('showTaxes', printOptions.showTax)
      .append('hideLineItemNotes', printOptions.hideLineItemNotes);

    return this.httpGetFile("/report/changeOrder/" + changeOrderId + '?' + params.toString());
  }

  printInvoice(quoteId: number, changeOrderId: number, printOptions: PrintChangeOrderOptions): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .set('timezone', timezone.toString())
      .append('locale', printOptions.locale)
      .append('showTaxes', printOptions.showTax)
      .append('combinedInvoice', printOptions.combinedInvoice);

    if (changeOrderId != null) params = params.append('changeOrderId', changeOrderId.toString());

    return this.httpGetFile("/report/jobInvoice/" + quoteId + '?' + params.toString());
  }

  changeOrderAnalysis(changeOrderId: number): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    return this.httpGetFile("/report/changeOrderAnalysis/" + changeOrderId + '?timezone=' + timezone);
  }

  saveJobCosting(quoteId: number, areaSummaries: JobCostEdit): Observable<number> {
    return this.httpPost<number>("/job/costing/" + quoteId, areaSummaries);
  }

  jobCostCalculator(quoteId: number): Observable<Blob> {
    return this.httpGetFile("/job/costCalculator/" + quoteId);
  }

  copyChangeOrder(changeOrderId: number, changeOrder: ChangeOrder): Observable<number> {
    return this.httpPost<number>("/job/copyChangeOrder/" + changeOrderId, changeOrder);
  }

  updateChangeOrder(changeOrder: ChangeOrder): Observable<number> {
    if (changeOrder.changeOrder_ID) {
      return this.httpPut<number>("/job/changeOrder/" + changeOrder.changeOrder_ID, changeOrder);
    } else {
      return this.httpPost<number>("/job/changeOrder", changeOrder);
    }
  }

  acknowledgeChangeOrder(quoteId: number, changeOrderId: number, status: number): Observable<number> {
    const params = new HttpParams()
      .set('quoteId', quoteId)
      .append('isAccepted', status);

    return this.httpPut<number>(`/job/changeOrder/${changeOrderId}/acknowledgement?${params.toString() }`, {});
  }


  deleteChangeOrder(changeOrderId: number): Observable<number> {
    return this.httpDelete<number>("/job/changeOrder/" + changeOrderId);
  }

  deleteChangeOrderItem(changeOrderItemId: number): Observable<number> {
    return this.httpDelete<number>("/job/changeOrderItem/" + changeOrderItemId);
  }

  saveChangeOrderItem(changeOrderItem: ChangeOrderItem): Observable<number> {
    return this.httpPost<number>("/job/changeOrderItem/", changeOrderItem);
  }

  saveTaxOptions(taxOptions: TaxOptionsDialogData): Observable<number> {
    return this.httpPut<number>(`/job/${taxOptions.changeOrder_ID}/taxOptions`, taxOptions);
  }

  sendArrivalNotices(notificationData: NotificationData) {
    return this.httpPost<number>("/job/sendNotification/", notificationData);
  }

  removeQuoteArea(changeOrderId: number, quoteAreaId: number): Observable<number> {
    return this.httpDelete<number>("/job/quoteArea/" + changeOrderId + '/' + quoteAreaId);
  }

  updateChangeOrderBidAmount(changeOrderId: number, bidAmount: number): Observable<ChangeOrder> {
    const params = new HttpParams()
      .set('bidAmount', bidAmount.toString());

    return this.httpPost<ChangeOrder>("/job/bidAmount/" + changeOrderId + '?' + params.toString(), {});
  }

  updateJobLostStatus(quoteId: number, job: Job): Observable<number> {
    return this.httpPost<number>("/job/lostStatus/" + quoteId, job);
  }

  updateJobManuallyCompleted(quoteId: number, job: Job): Observable<number> {
    return this.httpPost<number>("/job/manuallyComplete/" + quoteId, job);
  }

  updateChangeOrderMargin(changeOrderId: number, grossMargin: number): Observable<ChangeOrder> {
    const params = new HttpParams()
      .set('grossMargin', grossMargin.toString());

    return this.httpPost<ChangeOrder>("/job/grossMargin/" + changeOrderId + '?' + params.toString(), {});
  }
}
